import React, { useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, PhoneIncoming, Mic, MicOff, Pause, Play, Minus } from "lucide-react";
import { useTwilioVoice } from "@/context/TwilioVoiceContext";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function Softphone() {
  const t = useTwilioVoice();
  const { lang } = useI18n();
  const { user } = useAuth();
  const L = lang === "el";
  const [dial, setDial] = useState("");
  const [minimised, setMinimised] = useState(true);
  const [seconds, setSeconds] = useState(0);
  const [balance, setBalance] = useState({ allocated: 0, used: 0 });
  const tick = useRef(null);

  // Refresh talk-minutes balance every 30s while logged in
  useEffect(() => {
    if (!user || (user.role !== "sales_rep" && user.role !== "company_owner")) return;
    const load = async () => {
      try {
        const { data } = await api.get("/auth/me");
        const u = data.user || {};
        setBalance({
          allocated: u.talk_minutes_allocated || 0,
          used: u.talk_minutes_used || 0,
        });
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [user]);

  // Open automatically on incoming
  useEffect(() => {
    if (t.incomingCall) setMinimised(false);
  }, [t.incomingCall]);

  // Call duration timer
  useEffect(() => {
    if (t.status === "in-call") {
      setSeconds(0);
      tick.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } else {
      clearInterval(tick.current);
      setSeconds(0);
    }
    return () => clearInterval(tick.current);
  }, [t.status]);

  if (!t.enabled) return null;

  const onDial = () => {
    let num = dial.trim();
    if (!num) return;
    // basic normalisation: if doesn't start with +, assume Greece +30
    if (!num.startsWith("+")) num = num.startsWith("00") ? `+${num.slice(2)}` : `+30${num}`;
    t.startCall(num);
    setMinimised(false);
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const statusLabel = () => {
    if (t.status === "ringing") return L ? "Εισερχόμενη..." : "Incoming...";
    if (t.status === "connecting") return L ? "Σύνδεση..." : "Connecting...";
    if (t.status === "in-call") return fmt(seconds);
    if (t.deviceReady) return L ? "Έτοιμο" : "Ready";
    return L ? "Σύνδεση..." : "Connecting...";
  };

  // Floating bubble when minimised
  if (minimised) {
    return (
      <button
        data-testid="softphone-fab"
        onClick={() => setMinimised(false)}
        className={`fixed bottom-24 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white transition-all z-40 ${
          t.outOfMinutes ? "bg-red-500" :
          t.status === "in-call" ? "bg-emerald-600 animate-pulse" :
          t.status === "ringing" ? "bg-amber-500 animate-pulse" :
          t.deviceReady ? "bg-[#0052CC] hover:bg-[#0043A8]" : "bg-slate-400"
        }`}
        title={t.outOfMinutes ? (L ? "Τέλος λεπτών — επικοινωνία με admin" : "Out of minutes") : statusLabel()}
      >
        {t.status === "ringing" ? <PhoneIncoming className="w-6 h-6" /> : <Phone className="w-6 h-6" />}
      </button>
    );
  }

  return (
    <div className="fixed bottom-24 right-6 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-40 overflow-hidden" data-testid="softphone-panel">
      <header className="px-4 py-2.5 bg-slate-900 text-white flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full ${t.deviceReady ? "bg-emerald-400" : "bg-amber-400"}`} />
          <span className="text-xs font-semibold tracking-wider uppercase">Softphone</span>
          <span className="text-[10px] text-slate-300">· {t.fromNumber || "—"}</span>
        </div>
        <div className="flex items-center gap-2">
          {balance.allocated > 0 && (
            <span
              data-testid="softphone-balance"
              className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-bold ${
                balance.allocated - balance.used <= 0
                  ? "bg-red-500/30 text-red-200"
                  : balance.allocated - balance.used < 30
                    ? "bg-amber-500/30 text-amber-200"
                    : "bg-emerald-500/30 text-emerald-200"
              }`}
              title={L ? "Υπόλοιπα λεπτά ομιλίας" : "Talk minutes remaining"}
            >
              {Math.max(balance.allocated - balance.used, 0)}min
            </span>
          )}
          <button onClick={() => setMinimised(true)} className="p-1 hover:bg-white/10 rounded" title="Minimise">
            <Minus className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      <div className="p-4">
        {/* Out of minutes — block panel */}
        {t.outOfMinutes && (
          <div className="text-center py-4 space-y-2" data-testid="softphone-no-minutes">
            <div className="w-12 h-12 mx-auto rounded-full bg-red-100 text-red-600 flex items-center justify-center">
              <PhoneOff className="w-6 h-6" />
            </div>
            <div className="font-heading font-semibold text-slate-900">{L ? "Τέλος λεπτών ομιλίας" : "Out of talk minutes"}</div>
            <div className="text-xs text-slate-600 px-2">
              {L ? "Παρακαλώ επικοινώνησε με τον διαχειριστή της εταιρείας σου για να φορτώσει νέα λεπτά." : "Please contact your administrator to top up."}
            </div>
          </div>
        )}

        {/* Incoming call */}
        {!t.outOfMinutes && t.incomingCall && t.status === "ringing" && (
          <div className="text-center py-4 space-y-3" data-testid="softphone-incoming">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{L ? "Εισερχόμενη κλήση" : "Incoming call"}</div>
            <div className="font-heading text-xl font-bold text-slate-900">{t.incomingCall.parameters?.From || "—"}</div>
            <div className="flex justify-center gap-3 pt-2">
              <button data-testid="softphone-accept" onClick={t.acceptIncoming} className="w-12 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center shadow-md">
                <Phone className="w-5 h-5" />
              </button>
              <button data-testid="softphone-reject" onClick={t.rejectIncoming} className="w-12 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center shadow-md">
                <PhoneOff className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* In-call controls */}
        {!t.outOfMinutes && t.status === "in-call" && (
          <div className="text-center space-y-3" data-testid="softphone-incall">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold">
              {t.onHold ? (
                <span className="text-amber-600">{L ? "Σε αναμονή" : "On hold"}</span>
              ) : (
                <span className="text-slate-500">{L ? "Σε κλήση" : "On call"}</span>
              )}
            </div>
            <div className={`font-mono text-2xl font-bold tabular-nums ${t.onHold ? "text-amber-600" : "text-slate-900"}`}>{fmt(seconds)}</div>
            <div className="flex justify-center gap-2.5 pt-2">
              <button
                data-testid="softphone-mute"
                onClick={t.toggleMute}
                disabled={t.onHold}
                title={L ? "Σίγαση μικροφώνου" : "Mute microphone"}
                className={`w-11 h-11 rounded-full flex items-center justify-center shadow transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${t.muted && !t.onHold ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                {t.muted && !t.onHold ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              <button
                data-testid="softphone-hold"
                onClick={t.toggleHold}
                title={L ? "Αναμονή κλήσης" : "Hold call"}
                className={`w-11 h-11 rounded-full flex items-center justify-center shadow transition-colors ${t.onHold ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                {t.onHold ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              </button>
              <button
                data-testid="softphone-hangup"
                onClick={t.hangup}
                title={L ? "Τερματισμός" : "End call"}
                className="w-11 h-11 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center shadow-md"
              >
                <PhoneOff className="w-5 h-5" />
              </button>
            </div>
            <div className="flex justify-center gap-4 pt-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <span className="w-11 text-center">{L ? "Σίγαση" : "Mute"}</span>
              <span className="w-11 text-center">{L ? "Αναμονή" : "Hold"}</span>
              <span className="w-11 text-center">{L ? "Κλείσιμο" : "End"}</span>
            </div>
          </div>
        )}

        {/* Idle / dialer */}
        {!t.outOfMinutes && (t.status === "idle" || t.status === "connecting") && !t.incomingCall && (
          <>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{L ? "Κατάσταση" : "Status"}</div>
            <div className="text-sm font-semibold text-slate-900 mb-3">{statusLabel()}</div>
            <input
              data-testid="softphone-input"
              type="tel"
              value={dial}
              onChange={(e) => setDial(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onDial()}
              placeholder={L ? "Αριθμός (+30…)" : "Number (+30…)"}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/40 focus:border-[#0052CC]"
            />
            <button
              data-testid="softphone-call"
              onClick={onDial}
              disabled={!t.deviceReady || !dial.trim() || t.status === "connecting"}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-md text-sm font-semibold disabled:opacity-50"
            >
              <Phone className="w-4 h-4" /> {t.status === "connecting" ? (L ? "Σύνδεση..." : "Connecting...") : (L ? "Κλήση" : "Call")}
            </button>
            {t.error && (
              <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-1.5 rounded">{t.error}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
